import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import MediaAssets from '../components/MediaAssets';
import { 
  useGetTemplateQuery,
  useCreateTemplateMutation, 
  useUpdateTemplateMutation
} from '../store/services/whatsappApi';
import { ExtendedTemplate, TemplateFormData } from '../types/template';

// Helper function to check if a template name follows WhatsApp requirements
const isValidWhatsappTemplateName = (name: string): boolean => {
  return /^[a-z][a-z0-9_]*$/.test(name);
};

// Helper function to format a template name according to WhatsApp requirements
const formatWhatsappTemplateName = (name: string): string => {
  // Convert to lowercase
  let formatted = name.toLowerCase();
  
  // Replace any character that is not a lowercase letter, number or underscore
  formatted = formatted.replace(/[^a-z0-9_]/g, '_');
  
  // Ensure name starts with a letter
  if (!formatted || !(/^[a-z]/).test(formatted)) {
    formatted = 'template_' + formatted;
  }
  
  // Remove consecutive underscores
  formatted = formatted.replace(/_+/g, '_');
  
  // Remove trailing underscores
  formatted = formatted.replace(/_+$/g, '');
  
  return formatted;
};

// Using TemplateFormData from types/template.ts

const TemplateCreate: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  
  // State for managing detected variables and their examples
  const [detectedVariables, setDetectedVariables] = useState<string[]>([]);
  const [variableExamples, setVariableExamples] = useState<{[key: string]: string}>({});
  
  // State for managing template type and media
  const [templateType, setTemplateType] = useState<string>('text');
  const [headerFormat, setHeaderFormat] = useState<string>('TEXT');
  const [selectedMediaHandle, setSelectedMediaHandle] = useState<string>('');
  const [selectedMediaUrl, setSelectedMediaUrl] = useState<string>('');  // Track media URL for preview
  const [previewMessage, setPreviewMessage] = useState<string>('');

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<TemplateFormData>();
  
  // Watch template name and content for real-time formatting and preview
  const templateName = watch('name');
  const templateContent = watch('content');
  const headerText = watch('header_text');
  
  // Fetch template data if in edit mode
  const { data: templateData, isLoading: isLoadingTemplate } = useGetTemplateQuery(
    parseInt(id || '0'), 
    { skip: !isEditMode }
  ) as { data: ExtendedTemplate | undefined, isLoading: boolean };
  
  const [createTemplate, { isLoading: isCreating }] = useCreateTemplateMutation();
  const [updateTemplate, { isLoading: isUpdating }] = useUpdateTemplateMutation();

  // Function to extract variables from template content
  const extractVariables = (content: string): string[] => {
    if (!content) return [];
    
    // Regular expression to match variables inside {{}}
    const variableRegex = /\{\{([^}]+)\}\}/g;
    
    // Extract variable names and remove duplicates
    const variables = new Set<string>();
    let match;
    while ((match = variableRegex.exec(content)) !== null) {
      if (match[1]) {
        variables.add(match[1].trim());
      }
    }
    
    return Array.from(variables);
  };

  // Update preview message when template content or variables change
  useEffect(() => {
    let preview = templateContent || '';
    
    // Replace variables with their example values
    if (preview) {
      Object.entries(variableExamples).forEach(([variable, example]) => {
        const regex = new RegExp(`\\{\\{${variable}\\}\\}`, 'g');
        preview = preview.replace(regex, example || `[${variable}]`);
      });
    }
    
    setPreviewMessage(preview);
  }, [templateContent, variableExamples]);

  // Update variables field when template content changes
  useEffect(() => {
    if (templateContent) {
      const extractedVars = extractVariables(templateContent);
      if (extractedVars.length > 0) {
        setValue('variables', extractedVars.join(', '));
        
        // Update detected variables state
        setDetectedVariables(extractedVars);
        
        // Initialize examples for any new variables
        setVariableExamples(prev => {
          const updatedExamples = {...prev};
          extractedVars.forEach(varName => {
            if (!updatedExamples[varName]) {
              updatedExamples[varName] = ''; // Default empty example
            }
          });
          return updatedExamples;
        });
      } else {
        setDetectedVariables([]);
      }
    }
  }, [templateContent, setValue]);
  
  // Load template data in edit mode
  useEffect(() => {
    if (isEditMode && templateData) {
      // Set template type and header format from existing template
      // Use default values when fields don't exist in the API response
      const templateType = (templateData as ExtendedTemplate).type || 'text';
      const headerFormat = (templateData as ExtendedTemplate).header_format || 'TEXT';
      const mediaHandle = (templateData as ExtendedTemplate).media_handle || '';
      const headerText = (templateData as ExtendedTemplate).header_text || '';
      
      setTemplateType(templateType);
      setHeaderFormat(headerFormat);
      setSelectedMediaHandle(mediaHandle);
      
      reset({
        name: templateData.name,
        content: templateData.content,
        category: templateData.category,
        variables: templateData.variables?.join(', '),
        type: templateType,
        header_format: headerFormat,
        header_text: headerText,
        media_handle: mediaHandle
      });
      
      // Initialize detected variables from template
      const extractedVars = templateData.variables || [];
      setDetectedVariables(extractedVars);
      
      // Initialize examples from template or with empty values
      const initialExamples: {[key: string]: string} = {};
      extractedVars.forEach((varName: string) => {
        // Use existing examples if available or empty string
        initialExamples[varName] = ((templateData as ExtendedTemplate).variableExamples?.[varName]) || '';
      });
      setVariableExamples(initialExamples);
    }
  }, [isEditMode, templateData, reset]);
  
  // Helper function to update a specific variable example
  const updateVariableExample = (variable: string, value: string) => {
    setVariableExamples(prev => ({
      ...prev,
      [variable]: value
    }));
  };
  
  const onSubmit = async (data: TemplateFormData) => {
    try {
      // Check if all detected variables have examples
      const missingExamples = detectedVariables.filter(variable => 
        !variableExamples[variable] || variableExamples[variable].trim() === ''
      );
      
      if (missingExamples.length > 0) {
        alert(`Please provide examples for all variables: ${missingExamples.join(', ')}`);
        return;
      }
      
      // Format the template name according to WhatsApp requirements
      const formattedName = formatWhatsappTemplateName(data.name);
      
      // Parse variables if provided
      let parsedVariables: string[] = [];
      if (data.variables) {
        try {
          parsedVariables = data.variables.split(',').map(v => v.trim());
        } catch (error) {
          console.error('Error parsing variables:', error);
        }
      }
      
      // Validate media template requirements
      if (templateType === 'media') {
        // For media templates, validate based on header format
        if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat) && !selectedMediaHandle) {
          alert(`Please select a media asset for your ${headerFormat.toLowerCase()} header.`);
          return;
        }
        
        if (headerFormat === 'TEXT' && (!data.header_text || data.header_text.trim() === '')) {
          alert('Please enter header text for your text header.');
          return;
        }
      }

      const templateData = {
        ...data,
        name: formattedName, // Use the properly formatted name
        variables: parsedVariables,
        variableExamples: variableExamples, // Include the variable examples
        type: templateType,
        header_format: headerFormat,
        media_handle: selectedMediaHandle,
        // language_code will come from the form data (user selection)
        ...(isEditMode && { id: parseInt(id!) })
      };
      
      // If name was formatted differently, notify the user
      if (formattedName !== data.name) {
        console.log(`Template name was formatted from "${data.name}" to "${formattedName}" to meet WhatsApp requirements`);
      }

      if (isEditMode) {
        await updateTemplate(templateData).unwrap();
      } else {
        await createTemplate(templateData).unwrap();
      }
      
      // Redirect to templates list after successful save
      navigate('/templates');
    } catch (error) {
      console.error('Error saving template', error);
    }
  };

  // Handle media selection in the MediaAssets component
  const handleMediaSelect = (mediaHandle: string, fileType: string, mediaUrl?: string) => {
    setSelectedMediaHandle(mediaHandle);
    setSelectedMediaUrl(mediaUrl || '');
    setValue('media_handle', mediaHandle);
    
    // If we select a media file, update the template type and header format
    if (fileType === 'image') {
      setTemplateType('media');
      setHeaderFormat('IMAGE');
      setValue('type', 'media');
      setValue('header_format', 'IMAGE');
    } else if (fileType === 'video') {
      setTemplateType('media');
      setHeaderFormat('VIDEO');
      setValue('type', 'media');
      setValue('header_format', 'VIDEO');
    } else if (fileType === 'document') {
      setTemplateType('media');
      setHeaderFormat('DOCUMENT');
      setValue('type', 'media');
      setValue('header_format', 'DOCUMENT');
    }
  };
  
  // Handle template type change
  const handleTemplateTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value;
    setTemplateType(newType);
    setValue('type', newType);
    
    // Reset header format for text templates
    if (newType === 'text') {
      setHeaderFormat('TEXT');
      setValue('header_format', 'TEXT');
      setSelectedMediaHandle('');
      setSelectedMediaUrl('');
      setValue('media_handle', '');
    }
  };
  
  // Handle header format change
  const handleHeaderFormatChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFormat = e.target.value;
    setHeaderFormat(newFormat);
    setValue('header_format', newFormat);
    
    // Reset media handle when switching formats to avoid mismatched media types
    // For TEXT or LOCATION, no media is needed; for IMAGE/VIDEO/DOCUMENT, ensure fresh selection
    setSelectedMediaHandle('');
    setSelectedMediaUrl('');
    setValue('media_handle', '');
  };

  if (isEditMode && isLoadingTemplate) {
    return (
      <MainLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-whatsapp-green"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-whatsapp-teal">
            {isEditMode ? 'Edit Template' : 'Create New Template'}
          </h1>
          <button
            onClick={() => navigate('/templates')}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
            Back to Templates
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Column - Template Form */}
          <div className="w-full lg:w-1/2 bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4">Template Details</h2>
            
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                  Template Name
                </label>
                <input
                  type="text"
                  id="name"
                  className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-whatsapp-green ${templateName && !isValidWhatsappTemplateName(templateName) ? 'border-red-500' : ''}`}
                  placeholder="e.g., welcome_message"
                  {...register("name", { 
                    required: "Template name is required", 
                    validate: {
                      whatsappFormat: (value) => isValidWhatsappTemplateName(value) || "Template name must contain only lowercase letters, numbers, and underscores, and must start with a letter"
                    }
                  })}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
                
                {templateName && !isValidWhatsappTemplateName(templateName) && (
                  <div className="mt-2">
                    <p className="text-amber-500 text-xs mb-1">
                      <strong>Warning:</strong> Template name doesn't meet WhatsApp requirements. WhatsApp requires names with only lowercase letters, numbers, and underscores, starting with a letter.
                    </p>
                    <p className="text-xs text-gray-600">Suggested format: <span className="font-mono bg-gray-100 px-1 rounded">{formatWhatsappTemplateName(templateName)}</span></p>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="language_code">
                  Language
                </label>
                <select
                  id="language_code"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-whatsapp-green"
                  {...register("language_code", { required: "Language is required" })}
                  defaultValue="en_US"
                >
                  <option value="en">English (en)</option>
                  <option value="en_US">English US (en_US)</option>
                  <option value="en_GB">English UK (en_GB)</option>
                  <option value="es">Spanish (es)</option>
                  <option value="fr">French (fr)</option>
                  <option value="de">German (de)</option>
                  <option value="pt_BR">Portuguese Brazil (pt_BR)</option>
                  <option value="ar">Arabic (ar)</option>
                  <option value="hi">Hindi (hi)</option>
                  <option value="sw">Swahili (sw)</option>
                </select>
                {errors.language_code && <p className="text-red-500 text-xs mt-1">{errors.language_code.message}</p>}
                <p className="text-gray-500 text-xs mt-1">
                  Select the language this template will be approved for in WhatsApp
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="category">
                  Category
                </label>
                <select
                  id="category"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-whatsapp-green"
                  {...register("category", { required: "Category is required" })}
                >
                  <option value="UTILITY">UTILITY</option>
                  <option value="MARKETING">MARKETING</option>
                  <option value="AUTHENTICATION">AUTHENTICATION</option>
                </select>
                {errors.category && <p className="text-red-500 text-xs mt-1">{errors.category.message}</p>}
              </div>
              
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="type">
                  Template Type
                </label>
                <select
                  id="type"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-whatsapp-green"
                  {...register("type")}
                  value={templateType}
                  onChange={handleTemplateTypeChange}
                >
                  <option value="text">Text Only</option>
                  <option value="media">Media (Image, Video, Document)</option>
                </select>
                <p className="text-gray-500 text-xs mt-1">
                  Choose "Media" if you want to include images, videos, or documents in your template
                </p>
              </div>

              {/* Media Template Options */}
              {templateType === 'media' && (
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="header_format">
                    Header Format
                  </label>
                  <select
                    id="header_format"
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-whatsapp-green"
                    {...register("header_format")}
                    value={headerFormat}
                    onChange={handleHeaderFormatChange}
                  >
                    <option value="TEXT">Text</option>
                    <option value="IMAGE">Image</option>
                    <option value="VIDEO">Video</option>
                    <option value="DOCUMENT">Document</option>
                    <option value="LOCATION">Location</option>
                  </select>
                </div>
              )}
              
              {/* Text Header Input */}
              {templateType === 'media' && headerFormat === 'TEXT' && (
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="header_text">
                    Header Text
                  </label>
                  <input
                    type="text"
                    id="header_text"
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-whatsapp-green"
                    placeholder="Your header text here..."
                    {...register("header_text")}
                  />
                  <p className="text-gray-500 text-xs mt-1">
                    Header text cannot contain variables
                  </p>
                </div>
              )}
              
              {/* Media Asset Selector */}
              {templateType === 'media' && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat) && (
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Select Media for Header
                  </label>
                  {selectedMediaHandle ? (
                    <div className="flex items-center mb-2">
                      <div className="bg-green-100 text-green-600 px-3 py-1 rounded-md flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Media selected
                      </div>
                      <button 
                        type="button" 
                        onClick={() => {
                          setSelectedMediaHandle('');
                          setSelectedMediaUrl('');
                          setValue('media_handle', '');
                        }}
                        className="ml-2 text-red-500 hover:text-red-700"
                        title="Clear selection"
                      >  
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <p className="text-amber-500 text-sm mb-2">
                      Please select a media asset below. Accepted type: {headerFormat.toLowerCase()} (max 16MB)
                    </p>
                  )}
                  <MediaAssets 
                    onSelectMedia={handleMediaSelect} 
                    allowedType={headerFormat === 'IMAGE' ? 'image' : headerFormat === 'VIDEO' ? 'video' : 'document'}
                    requireResumableHandle
                  />
                </div>
              )}
              {templateType === 'media' && headerFormat === 'LOCATION' && (
                <div className="mb-4 p-3 rounded border border-blue-200 bg-blue-50">
                  <p className="text-sm text-blue-700">
                    Location headers do not require media uploads. You can provide the location details when sending the message.
                  </p>
                </div>
              )}
              
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="content">
                  Template Content
                </label>
                <textarea
                  id="content"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-whatsapp-green h-32"
                  placeholder="Hello {{name}}, thank you for..."
                  {...register("content", { required: "Template content is required" })}
                />
                {errors.content && <p className="text-red-500 text-xs mt-1">{errors.content.message}</p>}
                <p className="text-gray-500 text-xs mt-1">
                  Use {'{{'}<span>variable_name</span>{'}}' } for template variables
                </p>
              </div>

              <div className="mb-4">
                <label className="flex justify-between items-center text-gray-700 text-sm font-bold mb-2" htmlFor="variables">
                  <span>Variables</span>
                  <span className="text-xs font-normal text-whatsapp-green">Auto-detected from &#123;&#123;variable&#125;&#125;</span>
                </label>
                <input
                  type="text"
                  id="variables"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 bg-gray-50 leading-tight focus:outline-none focus:shadow-outline focus:border-whatsapp-green"
                  placeholder="Variables will be automatically detected"
                  readOnly
                  {...register("variables")}
                />
                <p className="text-gray-500 text-xs mt-1">
                  Variables are automatically extracted from your template content when you use &#123;&#123;variable_name&#125;&#125; syntax
                </p>
              </div>

              {/* Unsubscribe Footer Info (read-only) */}
              <div className="mb-4 p-3 rounded border border-amber-200 bg-amber-50">
                <p className="text-xs text-amber-800">
                  Footer is automatically added and cannot be changed: <span className="font-medium">"Reply with STOP to unsubscribe from whatsapp communication"</span>.
                  It will be placed after the body and before any buttons.
                </p>
              </div>
              
              {detectedVariables.length > 0 && (
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Variable Examples <span className="text-red-500">*</span>
                  </label>
                  <p className="text-gray-500 text-xs mb-3">
                    Please provide example values for each variable. These are required by WhatsApp for template approval.
                  </p>
                  
                  <div className="space-y-3">
                    {detectedVariables.map((variable, index) => (
                      <div key={index} className="flex flex-col">
                        <label className="text-sm text-gray-600 mb-1">
                          {variable}
                        </label>
                        <input
                          type="text"
                          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-whatsapp-green"
                          placeholder={`Example for ${variable}`}
                          value={variableExamples[variable] || ''}
                          onChange={(e) => updateVariableExample(variable, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => navigate('/templates')}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating || isUpdating}
                  className={`bg-whatsapp-green hover:bg-whatsapp-dark-green text-white py-2 px-4 rounded-md flex items-center ${(isCreating || isUpdating) ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {isCreating || isUpdating ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {isEditMode ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>{isEditMode ? 'Update Template' : 'Create Template'}</>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Right Column - Message Preview */}
          <div className="w-full lg:w-1/2">
            <div className="bg-gray-100 p-6 rounded-lg shadow-md h-full">
              <h2 className="text-xl font-semibold mb-4">Message Preview</h2>
              
              <div className="bg-whatsapp-chat-bg min-h-[400px] rounded-lg p-4 shadow-inner">
                <div className="flex flex-col">
                  {/* WhatsApp style message bubbles */}
                  <div className="max-w-xs sm:max-w-md ml-auto bg-whatsapp-light-green p-3 rounded-lg shadow mb-2 text-gray-700">
                    <p className="text-xs text-gray-500 mb-1">Template Preview</p>
                    
                    {/* Header preview */}
                    {templateType === 'media' && (
                      <div className="mb-2">
                        {headerFormat === 'TEXT' && headerText && (
                          <div className="font-bold">{headerText}</div>
                        )}
                        {headerFormat === 'IMAGE' && (
                          <div className="bg-gray-200 h-32 w-full rounded flex items-center justify-center mb-2 overflow-hidden">
                            {selectedMediaUrl ? (
                              <img 
                                src={selectedMediaUrl} 
                                alt="Template header" 
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            )}
                          </div>
                        )}
                        {headerFormat === 'VIDEO' && (
                          <div className="bg-gray-200 h-32 w-full rounded flex items-center justify-center mb-2 overflow-hidden">
                            {selectedMediaUrl ? (
                              <video 
                                src={selectedMediaUrl} 
                                className="w-full h-full object-cover"
                                controls
                                muted
                              />
                            ) : (
                              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            )}
                          </div>
                        )}
                        {headerFormat === 'DOCUMENT' && (
                          <div className="bg-gray-200 h-16 w-full rounded flex items-center justify-center mb-2">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            <span className="ml-2 text-gray-500">document.pdf</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Body preview */}
                    <div className="whitespace-pre-line">
                      {previewMessage || "Your message will appear here..."}
                    </div>
                    {/* Footer preview (auto-added) */}
                    <div className="text-gray-500 text-[11px] mt-2">
                      Reply with STOP to unsubscribe from whatsapp communication
                    </div>
                  </div>
                  
                  <div className="bg-white p-3 rounded-lg shadow max-w-xs mt-3">
                    <p className="text-gray-700">This is how your message will appear to the recipient.</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                <h3 className="text-blue-700 font-medium mb-2">Template Notes</h3>
                <ul className="list-disc pl-5 space-y-1 text-sm text-blue-600">
                  <li>All templates must be approved by WhatsApp before they can be used.</li>
                  <li>Approval usually takes 1-2 business days.</li>
                  <li>Make sure your template complies with WhatsApp's guidelines.</li>
                  <li>Templates containing promotional content should be categorized as MARKETING.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default TemplateCreate;
